import { useState } from "react";
import { useFetch } from "../../../common/hooks/useFetch";
import Swal from "sweetalert2";
import { createLegalProvisionDocumentService } from "../services/createLegalProvisionDocument.service";
import { deleteLegalProvisionDocumentService } from "../services/deleteLegalProvisionDocument.service";
import { updateLegalProvisionDocumentService } from "../services/updateLegalProvisionDocument.service";

const initialState = {
  accident: "",
  files: [],
};

const editInitialState = {
  accident: "",
  existingDocuments: [],
  documentIdsToDelete: [],
  newFiles: [],
};

export const useLegalProvisionDocumentCommand = (refresh) => {
  const fetchData = useFetch();
  const [errors, setErrors] = useState({ hasErrors: false, errors: {} });
  const [loading, setLoading] = useState(false);
  const [legalProvisionDocument, setLegalProvisionDocument] =
    useState(initialState);
  const [editData, setEditData] = useState(editInitialState);

  const handleChangeLegalProvisionDocument = ({ name, value }) => {
    setLegalProvisionDocument((prev) => {
      return { ...prev, [name]: value };
    });
  };

  const handleChangeEditData = ({ name, value }) => {
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const setEditDefaultValues = (item) => {
    setEditData({
      accident: item.id,
      existingDocuments: item.documents || [],
      documentIdsToDelete: [],
      newFiles: [],
    });
  };

  const markDocumentForDeletion = (docId) => {
    setEditData((prev) => ({
      ...prev,
      documentIdsToDelete: [...prev.documentIdsToDelete, docId],
      existingDocuments: prev.existingDocuments.filter((d) => d.id !== docId),
    }));
  };

  const addNewFile = (file) => {
    if (file) {
      setEditData((prev) => ({
        ...prev,
        newFiles: [...prev.newFiles, file],
      }));
    }
  };

  const createLegalProvisionDocument = async (legalProvisionDocument) => {
    try {
      setLoading(true);
      const isValid = validations(legalProvisionDocument);
      if (!isValid) return;

      const formData = new FormData();
      formData.append("accident", legalProvisionDocument.accident);
      legalProvisionDocument.files.forEach((file) => {
        formData.append("files", file);
      });

      const response = await createLegalProvisionDocumentService(fetchData)(
        formData
      );

      refresh?.();
      Swal.fire({
        icon: "success",
        title: "Documento de provisión legal registrado",
        text: response.message,
      });
      setLegalProvisionDocument(initialState);
      setErrors({ hasErrors: false, errors: {} });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error al registrar el documento de provisión legal",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateLegalProvisionDocument = async () => {
    try {
      const remainingDocs = editData.existingDocuments.length;
      const newDocs = editData.newFiles.length;
      if (remainingDocs === 0 && newDocs === 0) {
        setErrors({
          hasErrors: true,
          errors: { files: "Debe mantener o agregar al menos un documento." },
        });
        return;
      }

      if (!editData.accident) {
        setErrors({
          hasErrors: true,
          errors: { accident: "El campo 'Siniestro' es obligatorio." },
        });
        return;
      }

      setLoading(true);
      const formData = new FormData();
      formData.append("accident", editData.accident);
      formData.append(
        "documentIdsToDelete",
        JSON.stringify(editData.documentIdsToDelete)
      );
      editData.newFiles.forEach((file) => {
        formData.append("files", file);
      });

      await updateLegalProvisionDocumentService(fetchData)(formData);

      refresh?.();
      Swal.fire({
        icon: "success",
        title: "Éxito",
        text: "Documentos actualizados correctamente",
        showConfirmButton: false,
        timer: 1500,
      });
      setEditData(editInitialState);
      setErrors({ hasErrors: false, errors: {} });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error al actualizar los documentos",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteLegalProvisionDocument = async (documentIds) => {
    try {
      const isSure = await Swal.fire({
        title: "¿Estás seguro?",
        text: "¿Deseas eliminar estos documentos de provisión legal?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!isSure.isConfirmed) return;

      setLoading(true);
      for (const id of documentIds) {
        await deleteLegalProvisionDocumentService(fetchData)(id);
      }

      refresh?.();
      Swal.fire({
        icon: "success",
        title: "Eliminado",
        text: "Documentos eliminados correctamente",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error al eliminar los documentos",
      });
    } finally {
      setLoading(false);
    }
  };

  const validations = (legalProvisionDocument) => {
    const errors = { hasErrors: false, errors: {} };

    if (!legalProvisionDocument.accident) {
      errors.hasErrors = true;
      errors.errors.accident = "El campo 'Siniestro' es obligatorio.";
    }

    if (legalProvisionDocument.files.length === 0) {
      errors.hasErrors = true;
      errors.errors.files = "Debe adjuntar al menos un archivo.";
    }

    setErrors(errors);
    return !errors.hasErrors;
  };

  return {
    legalProvisionDocument,
    setLegalProvisionDocument,
    handleChangeLegalProvisionDocument,
    createLegalProvisionDocument,
    updateLegalProvisionDocument,
    deleteLegalProvisionDocument,
    editData,
    setEditDefaultValues,
    handleChangeEditData,
    markDocumentForDeletion,
    addNewFile,
    setEditData,
    loading,
    errors,
  };
};
